/**
 * Main application component
 */
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, ProtectedRoute } from './auth'
import { PortfolioFilterProvider } from './contexts'
import { Layout } from './components/Layout'
import {
  LoginPage,
  CallbackPage,
  SignupPage,
  DashboardPage,
  AnalyticsPage,
  HoldingsPage,
  WatchlistPage,
  PortfoliosPage,
  SettingsPage,
  BuySimulatorPage,
  PositionSizingPage,
  RebalancePreviewPage,
  ResearchPage,
  NewsPage,
  PhilosophyPage,
  ThesesPage,
  RulesPage,
  ThesisAllocationsPage,
  EarningsCalendarPage,
  DeployCapitalPage,
  AdminInvitesPage,
  AdminTenantsPage,
  AdminUsersPage,
} from './pages'

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      retry: 1,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <PortfolioFilterProvider>
            <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/callback" element={<CallbackPage />} />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DashboardPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AnalyticsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/holdings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <HoldingsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/watchlist"
              element={
                <ProtectedRoute>
                  <Layout>
                    <WatchlistPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolios"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PortfoliosPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Redirect old /tags route to /settings (tags tab) */}
            <Route path="/tags" element={<Navigate to="/settings" replace />} />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SettingsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Admin routes */}
            <Route
              path="/admin/tenants"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminTenantsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminUsersPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/invites"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminInvitesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Research routes */}
            <Route
              path="/research"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ResearchPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/research/:symbol"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ResearchPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/research/:symbol/:tab"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ResearchPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* News route */}
            <Route
              path="/news"
              element={
                <ProtectedRoute>
                  <Layout>
                    <NewsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Strategy routes */}
            <Route
              path="/strategy/philosophy"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PhilosophyPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/strategy/theses"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ThesesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/strategy/rules"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RulesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/strategy/allocations"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ThesisAllocationsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/strategy/earnings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EarningsCalendarPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/strategy/deploy"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DeployCapitalPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* What-If routes */}
            <Route
              path="/what-if/buy"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BuySimulatorPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/what-if/position"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PositionSizingPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/what-if/rebalance"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RebalancePreviewPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </PortfolioFilterProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
